/**
 * Analytics Filter Store
 * 
 * Persists analytics filter selections to localStorage.
 * - Default scenario: SSP585
 * - Default years: [2025, 2050, 2075, 2100] (milestone years)
 * - Default risk type: precip (precipitation)
 * - Max portfolio selection: Configurable (default: 5)
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { SSPScenario } from '@/types/api'

// ==========================================
// Configuration Constants
// ==========================================

/**
 * Maximum number of portfolios that can be selected at once.
 * Prevents excessive API requests and data loading.
 */
export const MAX_PORTFOLIO_SELECTION = 5

/**
 * Default scenario filters (SSP5-8.5 High Emissions)
 */
export const DEFAULT_SCENARIOS: SSPScenario[] = ['ssp585']

/**
 * Default year filters (milestone years for climate projections)
 */
export const DEFAULT_YEARS: number[] = [2025, 2050, 2075, 2100]

/**
 * Risk Type enum - 8 climate risk categories
 * Currently 'precip' and 'heat' have data, others are for future expansion
 */
export type RiskType = 'flood' | 'wind' | 'wildfire' | 'heat' | 'precip' | 'cold' | 'drought' | 'hail'

/**
 * Default risk type filter
 */
export const DEFAULT_RISK_TYPE: RiskType = 'hail'

/**
 * All available risk type options
 */
export const RISK_TYPE_OPTIONS: Array<{ value: RiskType; label: string; icon: string }> = [
    { value: 'flood', label: 'Flood', icon: '🌊' },
    { value: 'wind', label: 'Wind', icon: '🌬️' },
    { value: 'wildfire', label: 'Wildfire', icon: '🔥' },
    { value: 'heat', label: 'Heat', icon: '🌡️' },
    { value: 'precip', label: 'Precipitation', icon: '🌧️' },
    { value: 'cold', label: 'Cold', icon: '❄️' },
    { value: 'drought', label: 'Drought', icon: '☀️' },
    { value: 'hail', label: 'Hail', icon: '🌨️' },
]

/**
 * All available scenario options
 */
export const SCENARIO_OPTIONS: Array<{ value: SSPScenario; label: string }> = [
    { value: 'ssp126', label: 'SSP1-2.6 (Low)' },
    { value: 'ssp245', label: 'SSP2-4.5 (Medium)' },
    { value: 'ssp585', label: 'SSP5-8.5 (High)' },
]

/**
 * All available year options: 2020 to 2100, every 5 years
 */
export const YEAR_OPTIONS: number[] = [
    2020, 2025, 2030, 2035, 2040, 2045, 2050,
    2055, 2060, 2065, 2070, 2075, 2080, 2085, 2090, 2095, 2100
]

// ==========================================
// Store Types
// ==========================================

interface AnalyticsFilterState {
    // Filter values
    selectedPortfolios: number[]
    selectedScenarios: SSPScenario[]
    selectedYears: number[]
    selectedRiskType: RiskType

    // Actions
    setSelectedPortfolios: (portfolios: number[]) => void
    setSelectedScenarios: (scenarios: SSPScenario[]) => void
    setSelectedYears: (years: number[]) => void
    setSelectedRiskType: (riskType: RiskType) => void
    clearFilters: () => void

    // Utility
    hasActiveFilters: () => boolean
}

// ==========================================
// Store Implementation
// ==========================================

export const useAnalyticsFilterStore = create<AnalyticsFilterState>()(
    persist(
        (set, get) => ({
            // Initial state with defaults
            selectedPortfolios: [],
            selectedScenarios: DEFAULT_SCENARIOS,
            selectedYears: DEFAULT_YEARS,
            selectedRiskType: DEFAULT_RISK_TYPE,

            // Actions
            setSelectedPortfolios: (portfolios: number[]) => {
                // Enforce max selection limit
                const limitedPortfolios = portfolios.slice(0, MAX_PORTFOLIO_SELECTION)
                set({ selectedPortfolios: limitedPortfolios })
            },

            setSelectedScenarios: (scenarios) => {
                set({ selectedScenarios: scenarios })
            },

            setSelectedYears: (years) => {
                set({ selectedYears: years })
            },

            setSelectedRiskType: (riskType) => {
                set({ selectedRiskType: riskType })
            },

            clearFilters: () => {
                set({
                    selectedPortfolios: [],
                    selectedScenarios: DEFAULT_SCENARIOS,
                    selectedYears: DEFAULT_YEARS,
                    selectedRiskType: DEFAULT_RISK_TYPE,
                })
            },

            // Utility
            hasActiveFilters: () => {
                const state = get()
                return (
                    state.selectedPortfolios.length > 0 ||
                    state.selectedScenarios.length > 0 ||
                    state.selectedYears.length > 0 ||
                    state.selectedRiskType !== DEFAULT_RISK_TYPE
                )
            },
        }),
        {
            name: 'kcs-analytics-filters-v4',
            storage: createJSONStorage(() => localStorage),
            // Only persist filter values, not actions
            partialize: (state) => ({
                selectedPortfolios: state.selectedPortfolios,
                selectedScenarios: state.selectedScenarios,
                selectedYears: state.selectedYears,
                selectedRiskType: state.selectedRiskType,
            }),
        }
    )
)
